import { ContactDetailsType } from "#/api/contactDetails.type";
import { UpdateMedicalRecordRequestPayload } from "#/api/medical-record-requests.api";
import {
  MedicalRecordContactDetail,
  MedicalRecordRequest,
} from "#/api/medical-record-requests.type";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ContactFormValues } from "../components/EditRequestModal";
import { buildDetails } from "../components/formatRequest";
import useMedicalRecordRequest from "../hooks/useMedicalRecordRequest";
import { useUpdateContactDetail } from "../hooks/useUpdateContactDetail";
import { useUploadMedicalRecordRequestDocument } from "../hooks/useUploadMedicalRecordRequestDocument";
import { MedicalRecordRequestsContext } from "./MedicalRecordRequestsContext";

export type RecipientDetails = {
  name: string | null;
  type: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
} | null;

export type MedicalRecordRequestDetailContextValue = {
  request: MedicalRecordRequest | null;
  resolvedRequest: MedicalRecordRequest | null;
  details: ReturnType<typeof buildDetails>;
  recipientDetails: RecipientDetails;
  recipientType: string | null;
  recipientId: string | null;
  contactDetails: MedicalRecordContactDetail[];
  isEditable: boolean;
  canLookupFax: boolean;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  isLookupLoading: boolean;
  isLookupPolling: boolean;
  isContactSaving: boolean;
  isRegenerating: boolean;
  isUpdatingRequest: boolean;
  isStatusUpdating: boolean;
  isUpdatingStatus: boolean;
  isMarkReadyLoading: boolean;
  isMarkingReady: boolean;
  isSendLoading: boolean;
  isSendingNow: boolean;
  isUploadingDocument: boolean;
  isUpdatingContactDetail: boolean;
  isEditOpen: boolean;
  openEdit: () => void;
  closeEdit: () => void;
  isContactModalOpen: boolean;
  openContactModal: () => void;
  closeContactModal: () => void;
  isEditContactDetailOpen: boolean;
  openEditContactDetail: (contact: ContactDetailsType) => void;
  closeEditContactDetail: () => void;
  contactToEdit: ContactDetailsType | null;
  getEditInitialValues: () => UpdateMedicalRecordRequestPayload | undefined;
  handleSave: (formValues: UpdateMedicalRecordRequestPayload) => Promise<void>;
  handleLookupFax: () => Promise<void>;
  handleUseContactFax: (fax?: string | null) => Promise<void>;
  handleSaveContact: (contactForm: ContactFormValues) => Promise<void>;
  handleRegeneratePdf: () => Promise<void>;
  handleUploadDocument: (file: File) => Promise<void>;
  handleSaveExistingContact: (payload: {
    phone?: string | null;
    fax?: string | null;
    email?: string | null;
    providerName?: string | null;
    institutionName?: string | null;
    department?: string | null;
  }) => Promise<void>;
  handleUpdateStatus: (status: string) => Promise<void>;
  handleActionSelect: (action: string) => Promise<void>;
  onSendNow?: () => Promise<void>;
  onMarkReady?: () => Promise<void>;
  onLookupFax?: () => Promise<void>;
  onEditContactInfo?: () => void;
  onRegeneratePdf?: () => Promise<void>;
  onUploadDocument?: (file: File) => Promise<void>;
  onUpdateStatus?: (status: string) => Promise<void>;
  onActionSelect?: (action: string) => Promise<void>;
};

const MedicalRecordRequestDetailContext = createContext<
  MedicalRecordRequestDetailContextValue | undefined
>(undefined);

const LOOKUP_POLL_INTERVAL_MS = 7000;

type ProviderProps = {
  children: React.ReactNode;
  requestId?: string;
  request?: MedicalRecordRequest | null;
  allowEdit?: boolean;
};

export function MedicalRecordRequestDetailProvider({
  children,
  requestId,
  request,
  allowEdit,
}: ProviderProps) {
  const fallbackContext = useContext(MedicalRecordRequestsContext);
  const {
    data: fetchedRequest,
    isLoading: singleLoading,
    isError: singleError,
    error: singleErrorValue,
    refetch: refetchSingle,
  } = useMedicalRecordRequest(requestId, !request);

  const {
    requests,
    isLoading: contextLoading,
    isError: contextIsError,
    error: contextError,
  } = fallbackContext ?? {
    requests: [],
    isLoading: false,
    isError: false,
    error: null,
  };

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isContactModalOpen, setContactModalOpen] = useState(false);
  const [isEditContactDetailOpen, setEditContactDetailOpen] = useState(false);
  const [contactToEdit, setContactToEdit] = useState<ContactDetailsType | null>(
    null
  );
  const [isLookupPolling, setIsLookupPolling] = useState(false);
  const [lookupPollSnapshot, setLookupPollSnapshot] = useState<{
    requestId: string;
    fax: string | null;
    status: string | null;
  } | null>(null);

  const resolvedRequest = useMemo(() => {
    if (request) return request;
    if (fetchedRequest) return fetchedRequest;
    if (!requestId) return null;
    return requests.find((entry) => entry.id === requestId) ?? null;
  }, [fetchedRequest, request, requestId, requests]);

  const refreshSingleRequest = useCallback(async () => {
    if (!request && requestId) {
      await refetchSingle();
    }
  }, [refetchSingle, request, requestId]);

  const normalizedRecipientType =
    resolvedRequest?.recipientType || resolvedRequest?.recipient_type || null;
  const hasInstitutionDetails = Boolean(
    resolvedRequest?.recipientDetails?.institution?.id
  );
  const hasProviderDetails = Boolean(
    resolvedRequest?.recipientDetails?.provider?.id
  );
  const isInstitutionRecipient =
    normalizedRecipientType === "institution" ||
    (!normalizedRecipientType && hasInstitutionDetails && !hasProviderDetails);
  const normalizedRecipientId =
    resolvedRequest?.recipientExternalId ||
    resolvedRequest?.recipient_external_id ||
    (isInstitutionRecipient
      ? resolvedRequest?.recipientDetails?.institution?.id
      : resolvedRequest?.recipientDetails?.provider?.id) ||
    null;

  const markReady = fallbackContext?.markReadyToSend;
  const isMarkReadyLoading =
    Boolean(fallbackContext?.isMarking) &&
    fallbackContext?.markingRequestId === resolvedRequest?.id;
  const sendNow = fallbackContext?.sendRequest;
  const isSendLoading =
    Boolean(fallbackContext?.isSending) &&
    fallbackContext?.sendingRequestId === resolvedRequest?.id;
  const contactDetails: MedicalRecordContactDetail[] =
    (isInstitutionRecipient
      ? resolvedRequest?.recipientDetails?.institution?.contact_details
      : resolvedRequest?.recipientDetails?.provider?.contact_details) ??
    resolvedRequest?.recipientDetails?.institution?.contact_details ??
    resolvedRequest?.recipientDetails?.provider?.contact_details ??
    [];

  const isEditable = (allowEdit ?? true) && Boolean(fallbackContext);

  const isLookupLoading =
    ((Boolean(fallbackContext?.isLookupLoading) &&
      fallbackContext?.lookupRequestId === resolvedRequest?.id) ||
      isLookupPolling);
  const isContactSaving =
    Boolean(fallbackContext?.isSavingContact) &&
    fallbackContext?.savingContactRequestId === resolvedRequest?.id;
  const isRegenerating =
    Boolean(fallbackContext?.isRegenerating) &&
    fallbackContext?.regeneratingRequestId === resolvedRequest?.id;
  const isUpdatingRequest =
    Boolean(fallbackContext?.isUpdating) &&
    fallbackContext?.updatingRequestId === resolvedRequest?.id &&
    fallbackContext?.updatingSource === "edit";
  const isStatusUpdating =
    Boolean(fallbackContext?.isUpdating) &&
    fallbackContext?.updatingSource === "status" &&
    fallbackContext?.updatingRequestId === resolvedRequest?.id;
  const isLookupAllowed = isEditable && Boolean(normalizedRecipientId);

  useEffect(() => {
    if (!isLookupPolling || !resolvedRequest?.id) return;
    const intervalId = setInterval(() => {
      refreshSingleRequest();
    }, LOOKUP_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [isLookupPolling, refreshSingleRequest, resolvedRequest?.id]);

  useEffect(() => {
    if (!isLookupPolling || !lookupPollSnapshot || !resolvedRequest) return;
    if (lookupPollSnapshot.requestId !== resolvedRequest.id) {
      setIsLookupPolling(false);
      setLookupPollSnapshot(null);
      return;
    }

    const currentFax =
      resolvedRequest.faxNumber ?? resolvedRequest.fax_number ?? null;
    const currentStatus = resolvedRequest.status ?? null;

    const faxUpdated =
      lookupPollSnapshot.fax !== currentFax && Boolean(currentFax);
    const statusAdvanced =
      lookupPollSnapshot.status !== currentStatus &&
      currentStatus !== "lookup_in_progress";

    if (faxUpdated || statusAdvanced) {
      setIsLookupPolling(false);
      setLookupPollSnapshot(null);
    }
  }, [isLookupPolling, lookupPollSnapshot, resolvedRequest]);

  const { mutateAsync: uploadDocument, isPending: isUploadingDocument } =
    useUploadMedicalRecordRequestDocument();
  const {
    mutateAsync: updateContactDetail,
    isPending: isUpdatingContactDetail,
  } = useUpdateContactDetail();

  const handleOpenContactModal = () => setContactModalOpen(true);
  const handleOpenEditContactDetail = (contact: ContactDetailsType) => {
    setContactToEdit(contact);
    setEditContactDetailOpen(true);
  };

  const handleSaveExistingContact = async (payload: {
    phone?: string | null;
    fax?: string | null;
    email?: string | null;
    providerName?: string | null;
    institutionName?: string | null;
    department?: string | null;
  }) => {
    if (!contactToEdit?.id) return;
    const body: any = {
      phone: payload.phone,
      fax: payload.fax,
      email: payload.email,
      department: payload.department,
    };
    if (payload.providerName) {
      body.provider = { name: payload.providerName };
    }
    if (payload.institutionName) {
      body.institution = { name: payload.institutionName };
    }
    await updateContactDetail({ id: contactToEdit.id, payload: body });
    setEditContactDetailOpen(false);
    setContactToEdit(null);
    await refreshSingleRequest();
  };

  const handleUploadDocument = async (file: File) => {
    if (!resolvedRequest) return;
    await uploadDocument({ id: resolvedRequest.id, file });
    await refreshSingleRequest();
  };

  const handleSaveExistingRequestContact = async (
    contactForm: ContactFormValues
  ) => {
    if (!resolvedRequest || !fallbackContext) return;
    await fallbackContext.upsertContactDetail({
      request: resolvedRequest,
      contact: contactForm,
    });
    setContactModalOpen(false);
    await refreshSingleRequest();
  };

  const handleLookupFax = async () => {
    if (!resolvedRequest || !fallbackContext) return;
    setLookupPollSnapshot({
      requestId: resolvedRequest.id,
      fax: resolvedRequest.faxNumber ?? resolvedRequest.fax_number ?? null,
      status: resolvedRequest.status ?? null,
    });
    setIsLookupPolling(true);
    try {
      await fallbackContext.lookupRecipientFax(resolvedRequest);
      await refreshSingleRequest();
    } finally {
      setIsLookupPolling(false);
      setLookupPollSnapshot(null);
    }
  };

  const handleUseContactFax = async (fax?: string | null) => {
    if (!fax || !resolvedRequest || !fallbackContext) return;
    await fallbackContext.updateRequest({
      id: resolvedRequest.id,
      payload: { faxNumber: fax },
      source: "contact",
    });
    await refreshSingleRequest();
  };

  const handleRegeneratePdf = async () => {
    if (!resolvedRequest || !fallbackContext) return;
    await fallbackContext.regeneratePdf(resolvedRequest.id);
    await refreshSingleRequest();
  };

  const handleSaveExistingRequest = async (
    formValues: UpdateMedicalRecordRequestPayload
  ) => {
    if (!resolvedRequest || !fallbackContext) return;
    await fallbackContext.updateRequest({
      id: resolvedRequest.id,
      payload: formValues,
      source: "edit",
    });
    setIsEditOpen(false);
    await refreshSingleRequest();
  };

  const handleUpdateStatus = async (status: string) => {
    if (!resolvedRequest || !fallbackContext) return;
    await fallbackContext.updateRequest({
      id: resolvedRequest.id,
      payload: { status },
      source: "status",
      successMessage: "Status updated",
    });
    await refreshSingleRequest();
  };

  const handleActionSelect = async (action: string) => {
    if (!resolvedRequest) return;
    if (action === "send" && sendNow) {
      await sendNow(resolvedRequest.id);
      return;
    }
    if (action === "markReady" && markReady) {
      await markReady(resolvedRequest.id);
      return;
    }
    if (action === "editContact") {
      setIsEditOpen(true);
      return;
    }
    if (action === "regeneratePdf") {
      await handleRegeneratePdf();
      return;
    }
    if (action === "lookupFax") {
      await handleLookupFax();
    }
  };

  const getEditInitialValues = ():
    | UpdateMedicalRecordRequestPayload
    | undefined => {
    if (!resolvedRequest) return undefined;
    return {
      recipientName:
        resolvedRequest.recipientName ?? resolvedRequest.recipient_name ?? "",
      deliveryMethod:
        resolvedRequest.deliveryMethod ?? resolvedRequest.delivery_method ?? "",
      faxNumber: resolvedRequest.faxNumber ?? resolvedRequest.fax_number ?? "",
      emailAddress:
        resolvedRequest.emailAddress ?? resolvedRequest.email_address ?? "",
    };
  };

  const rawRecipientDetails =
    (resolvedRequest as any)?.recipientDetails ?? null;
  const recipientDetails: RecipientDetails = rawRecipientDetails
    ? {
        name:
          rawRecipientDetails.name ?? rawRecipientDetails.recipientName ?? null,
        type:
          rawRecipientDetails.type ?? rawRecipientDetails.recipientType ?? null,
        phone:
          rawRecipientDetails.phone ?? rawRecipientDetails.phoneNumber ?? null,
        email: rawRecipientDetails.email ?? null,
        address: rawRecipientDetails.address ?? null,
      }
    : null;
  const details = resolvedRequest ? buildDetails(resolvedRequest) : [];

  const effectiveRecipientType =
    normalizedRecipientType ??
    (isInstitutionRecipient
      ? "institution"
      : hasProviderDetails
      ? "provider"
      : null);

  const value: MedicalRecordRequestDetailContextValue = {
    request: resolvedRequest,
    resolvedRequest,
    details,
    recipientDetails,
    recipientType: effectiveRecipientType,
    recipientId: normalizedRecipientId,
    contactDetails,
    isEditable,
    canLookupFax: isLookupAllowed,
    isLoading: Boolean(!request && (singleLoading || contextLoading)),
    isError: Boolean(!request && (singleError || contextIsError)),
    errorMessage: singleError
      ? singleErrorValue instanceof Error
        ? singleErrorValue.message
        : "Unknown error"
      : contextError instanceof Error
      ? contextError.message
      : undefined,
    isLookupLoading,
    isLookupPolling,
    isContactSaving,
    isRegenerating,
    isUpdatingRequest,
    isStatusUpdating,
    isUpdatingStatus: isStatusUpdating,
    isMarkReadyLoading,
    isMarkingReady: isMarkReadyLoading,
    isSendLoading,
    isSendingNow: isSendLoading,
    isUploadingDocument,
    isUpdatingContactDetail,
    isEditOpen,
    openEdit: () => setIsEditOpen(true),
    closeEdit: () => setIsEditOpen(false),
    isContactModalOpen,
    openContactModal: handleOpenContactModal,
    closeContactModal: () => setContactModalOpen(false),
    isEditContactDetailOpen,
    openEditContactDetail: handleOpenEditContactDetail,
    closeEditContactDetail: () => {
      setEditContactDetailOpen(false);
      setContactToEdit(null);
    },
    contactToEdit,
    getEditInitialValues,
    handleSave: handleSaveExistingRequest,
    handleLookupFax,
    handleUseContactFax,
    handleSaveContact: handleSaveExistingRequestContact,
    handleRegeneratePdf,
    handleUploadDocument,
    handleSaveExistingContact,
    handleUpdateStatus,
    handleActionSelect,
    onSendNow: sendNow
      ? async () => {
          await sendNow(resolvedRequest?.id ?? "");
        }
      : undefined,
    onMarkReady: markReady
      ? async () => {
          await markReady(resolvedRequest?.id ?? "");
        }
      : undefined,
    onLookupFax: handleLookupFax,
    onEditContactInfo: isEditable ? () => setIsEditOpen(true) : undefined,
    onRegeneratePdf: handleRegeneratePdf,
    onUploadDocument: handleUploadDocument,
    onUpdateStatus: handleUpdateStatus,
    onActionSelect: handleActionSelect,
  };

  return (
    <MedicalRecordRequestDetailContext.Provider value={value}>
      {children}
    </MedicalRecordRequestDetailContext.Provider>
  );
}

export function useMedicalRecordRequestDetailContext() {
  const ctx = useContext(MedicalRecordRequestDetailContext);
  if (!ctx) {
    throw new Error(
      "useMedicalRecordRequestDetailContext must be used within MedicalRecordRequestDetailProvider"
    );
  }
  return ctx;
}
